import { useEffect, useState } from "react";
import { Copy, Link2, Plus, Trash2, Loader2, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

const PRESETS = [
  ["email", "Email", { utm_source: "email", utm_medium: "email" }],
  ["linkedin", "LinkedIn", { utm_source: "linkedin", utm_medium: "social" }],
  ["custom", "Custom", { utm_source: "", utm_medium: "" }],
];
const inputCls = "h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
export const shortUrl = (code) => `${window.location.origin}/s/${code}`;

export const ShortLinksPanel = ({ campaign: c }) => {
  const [links, setLinks] = useState([]);
  const [preset, setPreset] = useState("email");
  const [f, setF] = useState({ label: "Email", utm_source: "email", utm_medium: "email", utm_campaign: c.slug });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/campaigns/${c.id}/links`).then((r) => setLinks(r.data));
  useEffect(() => { load(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = ([k, label, utm]) => { setPreset(k); setF({ ...f, ...utm, label: k === "custom" ? "" : label }); };
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/links`, f);
      navigator.clipboard?.writeText(shortUrl(data.code)).catch(() => {});
      toast.success("Short link created and copied");
      load();
    } catch (ex) { toast.error(formatErr(ex)); } finally { setBusy(false); }
  };
  const remove = async (l) => { await api.delete(`/campaigns/${c.id}/links/${l.id}`); load(); };

  return (
    <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6 sv-rise" data-testid="short-links-panel">
      <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><Link2 className="w-4 h-4 text-indigo-400" /> Short links</h3>
      <p className="text-xs text-slate-500 mb-4">Shareable links with UTM tags pre-filled. Clicks are counted and the source shows in Traffic sources.</p>
      <form onSubmit={create} className="flex flex-wrap items-center gap-2 mb-5" data-testid="short-link-form">
        {PRESETS.map((p) => (
          <button key={p[0]} type="button" data-testid={`short-link-preset-${p[0]}`} onClick={() => pick(p)}
            className={`h-9 px-3 rounded-full text-xs border transition-colors ${preset === p[0] ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>{p[1]}</button>
        ))}
        <input data-testid="short-link-label-input" required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Label, e.g. Newsletter Oct" className={`${inputCls} w-44`} />
        <input data-testid="short-link-source-input" value={f.utm_source} onChange={(e) => setF({ ...f, utm_source: e.target.value })} placeholder="utm_source" className={`${inputCls} w-32 font-mono text-xs`} />
        <input data-testid="short-link-medium-input" value={f.utm_medium} onChange={(e) => setF({ ...f, utm_medium: e.target.value })} placeholder="utm_medium" className={`${inputCls} w-32 font-mono text-xs`} />
        <input data-testid="short-link-campaign-input" value={f.utm_campaign} onChange={(e) => setF({ ...f, utm_campaign: e.target.value })} placeholder="utm_campaign" className={`${inputCls} w-40 font-mono text-xs`} />
        <button data-testid="short-link-create-button" disabled={busy} className="h-9 px-4 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
        </button>
      </form>
      {links.length === 0 ? <p className="text-sm text-slate-500">No short links yet.</p> : (
        <ul className="divide-y divide-[#242C3D]" data-testid="short-links-list">
          {links.map((l) => (
            <li key={l.id} data-testid={`short-link-row-${l.code}`} className="py-3 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="text-sm text-slate-100">{l.label}</div>
                <div className="font-mono text-[11px] text-slate-500 truncate">{[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" · ") || "no UTM tags"}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-indigo-300" data-testid={`short-link-url-${l.code}`}>{shortUrl(l.code).replace(/^https?:\/\//, "")}</span>
                <span className="flex items-center gap-1 text-xs text-slate-400" data-testid={`short-link-clicks-${l.code}`}><MousePointerClick className="w-3.5 h-3.5" /> {l.clicks}</span>
                <button data-testid={`short-link-copy-${l.code}`} onClick={() => { navigator.clipboard.writeText(shortUrl(l.code)); toast.success("Link copied"); }} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><Copy className="w-4 h-4" /></button>
                <button data-testid={`short-link-delete-${l.code}`} onClick={() => remove(l)} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
